// Saving a capture result to a file instead of uploading it, for dev capture
// (`?dev=1`, see `devCaptureEnabled`). Without a JWT there are no presigned
// URLs, so the bytes have nowhere to go — but they are exactly what a capture
// test wants to look at, so they come down as a file.
//
// Format mirrors `tools/proto-ab`'s harness dump on purpose: one JSON with
// base64 payloads, unpacked by the same `tools/proto-ab/extract.py`. Two
// reasons. iOS Safari drops the 2nd and later downloads of a burst, so seven
// separate files would silently lose six. And a shared extractor means a
// prototype run and an SPA run land as the same filenames in two directories —
// directly diffable, which is the whole point of capturing them side by side.

/** One captured file, base64-encoded so it survives the JSON round-trip. */
export interface SavedFile {
  filename: string;
  contentType: string;
  /** Base64 of the raw bytes (no data: prefix). */
  data: string;
}

export interface ScanBundle {
  /** Discriminator `extract.py` uses to tell this from a proto-ab dump. */
  harness: "spa-dev";
  at: string;
  scanID: string;
  version: string;
  ua: string;
  /** Free-text note (whose foot, which run) — see `normaliseCaptureLabel`. */
  label?: string;
  files: SavedFile[];
}

/**
 * Clean a typed-in capture label into something safe to put in a path.
 *
 * The label's whole job is to group runs: the same text typed in this app and
 * in `tools/proto-ab` makes the two captures of one foot land in one folder on
 * HiDrive. Which means it reaches a remote path, so it is reduced to a
 * conservative alphabet here — and again, independently, on the server, which
 * cannot trust a browser. Returns undefined for anything that cleans away to
 * nothing, so an empty field behaves exactly like no field.
 */
export function normaliseCaptureLabel(raw: string): string | undefined {
  const cleaned = raw
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^A-Za-z0-9._-]/g, "")
    .slice(0, 40)
    // Separators off both ends, AFTER the cap: a leading dot would give a
    // traversal ("..") or an invisible folder, and a cut mid-word would leave a
    // trailing dash that no longer matches the same note typed into proto-ab.
    .replace(/^[._-]+|[._-]+$/g, "");
  return cleaned.length > 0 ? cleaned : undefined;
}

/** Base64 of a blob's bytes, chunked so a large JPEG can't blow the call stack. */
export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * Package capture outputs into the bundle written to disk. Takes the same
 * `RunInput`-shaped list the upload pipeline would have consumed, so the file
 * contains exactly what would have gone to S3 — including `capture-meta.json`
 * with whatever diagnostics it carries.
 */
export async function buildScanBundle(
  inputs: readonly { blob: Blob; filename: string; contentType: string }[],
  meta: { at: string; scanID: string; version: string; ua: string; label?: string },
): Promise<ScanBundle> {
  const files: SavedFile[] = [];
  for (const input of inputs) {
    files.push({
      filename: input.filename,
      contentType: input.contentType,
      data: await blobToBase64(input.blob),
    });
  }
  return { harness: "spa-dev", ...meta, files };
}

/**
 * Trigger the browser download. Best-effort and deliberately non-throwing: this
 * runs at the end of a capture the user just spent a minute on, and a failed
 * download must not surface as a crash. Returns the filename on success.
 */
export function downloadJson(name: string, json: string): string | null {
  try {
    const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    return name;
  } catch {
    return null;
  }
}

/** Where a bundle went when a capture-ingest token was present. */
export type IngestOutcome =
  | { kind: "pending" }
  | { kind: "spooled"; spooled: string; bytes: number }
  | { kind: "error"; detail: string };

/**
 * POST the bundle to the capture-ingest instead of downloading it, so a scan
 * taken on a phone reaches the reconstruction worker with no file handling in
 * between. Same JSON the download would have contained — the ingest spools the
 * body byte-for-byte, so the two paths hand the worker identical bytes.
 *
 * Non-throwing like `downloadJson`, and for the same reason: this runs at the
 * end of a capture the tester just spent a minute on. Any failure comes back as
 * an outcome so the caller can fall back to the download rather than lose the
 * shots.
 */
export async function postScanBundle(
  url: string,
  token: string,
  json: string,
): Promise<IngestOutcome> {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // Preferred over the ingest's `?token=` fallback: the token would
        // otherwise land in the proxy's access log. The preflight this costs is
        // answered by the ingest (and passed through by the proxy).
        Authorization: `Bearer ${token}`,
      },
      body: json,
    });
    const text = await res.text();
    if (!res.ok) {
      return { kind: "error", detail: `HTTP ${res.status} — ${text.slice(0, 200)}` };
    }
    const body = JSON.parse(text) as { spooled?: unknown; bytes?: unknown };
    return {
      kind: "spooled",
      spooled: typeof body.spooled === "string" ? body.spooled : "(unnamed)",
      bytes: typeof body.bytes === "number" ? body.bytes : json.length,
    };
  } catch (err) {
    return { kind: "error", detail: (err as Error).message };
  }
}
